import { Injectable } from '@angular/core'
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http'
import { Observable, catchError, map, switchMap, throwError } from 'rxjs'
import { environment } from '../../environments/environment.development'

export interface User {
  id: string
  name: string
  email: string
  role: string
  avatar: string
}

export interface Store {
  _id: string
  name: string
  username: string
  showOnStoreSite?: boolean
  storePrefix?: string
  category: string
  subcategory?: string
  bio?: string
  tagline?: string
  serviceArea?: string
  offlineLocations?: string[]
  deliveryNotes?: string
  priceClaim?: string
  phone?: string
  secondaryPhone?: string
  address?: string
  website?: string
  socialLinks?: {
    instagram?: string
    facebook?: string
    whatsapp?: string
  }
  highlights?: Array<{
    title: string
    image?: string
  }>
  isActive: boolean
  owner: {
    _id: string
    name: string
    email?: string
  }
  createdAt: string
  productCount: number
  followers?: number
  logo?: string
}

export interface Stats {
  totalStores: number
  totalProducts: number
  totalUsers: number
  approvedStores: number
  revenue: number
  growth: number | null
  growthRates?: {
    stores: number | null
    products: number | null
    users: number | null
    revenue: number | null
  }
  satisfaction: number
  uptime: number
}

export interface NextProductCodeResponse {
  store: string
  prefix: string
  nextSequence: number
  nextCode: string
}

export interface StoreSignupPayload {
  sellerName: string
  email: string
  password: string
  phone: string
  storeName: string
  storeUsername: string
  category: string
  bio: string
}

export interface StoreSignupResponse {
  success: boolean
  message: string
  user: { id: string; name: string; email: string; role: string }
  store: { id: string; name: string; username: string; status: string }
}

export interface UpdateStorePayload extends Partial<Store> {
  ownerPassword?: string
}

export interface LoginResponse {
  success: boolean
  message: string
  user: { id: string; name: string; email: string; role: string }
}

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private apiUrl = environment.apiUrl

  constructor(private http: HttpClient) {}

  // Health check
  healthCheck(): Observable<any> {
    return this.http.get(`${this.apiUrl}/health`)
  }

  // Auth
  login(identifier: string, password: string): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.apiUrl}/login`, {
      identifier,
      password
    })
  }

  userLogin(email: string, password: string): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.apiUrl}/user/login`, { email, password })
  }

  adminLogin(email: string, password: string): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.apiUrl}/admin/login`, { email, password })
  }

  logout(): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(`${this.apiUrl}/logout`, {})
  }

  getMe(): Observable<{ success: boolean; user: { id: string; name: string; email: string; role: string } }> {
    return this.http.get<{ success: boolean; user: { id: string; name: string; email: string; role: string } }>(`${this.apiUrl}/me`)
  }

  // Users
  getUsers(): Observable<User[]> {
    return this.http.get<User[]>(`${this.apiUrl}/users`)
  }

  // Stores
  getStores(params?: { category?: string; isActive?: boolean; username?: string; mine?: boolean }): Observable<Store[]> {
    let url = `${this.apiUrl}/stores`
    const queryParams = new URLSearchParams()
    if (params?.category) queryParams.set('category', params.category)
    if (params?.isActive !== undefined) queryParams.set('isActive', String(params.isActive))
    if (params?.username) queryParams.set('username', params.username)
    if (params?.mine) queryParams.set('mine', 'true')
    const qs = queryParams.toString()
    if (qs) url += `?${qs}`
    return this.http.get<Store[]>(url).pipe(
      map((stores) => this.deepSanitizeSignedS3Urls(stores))
    )
  }

  updateStore(id: string, data: UpdateStorePayload): Observable<Store> {
    return this.http.put<Store>(`${this.apiUrl}/stores/${id}`, data)
  }

  createAdminStore(data: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/admin/stores`, data)
  }

  // Products
  getProducts(params?: { store?: string; category?: string }): Observable<any[]> {
    let url = `${this.apiUrl}/products`
    const queryParams = new URLSearchParams()
    if (params?.store) queryParams.set('store', params.store)
    if (params?.category) queryParams.set('category', params.category)
    const qs = queryParams.toString()
    if (qs) url += `?${qs}`
    return this.http.get<any[]>(url).pipe(
      map((products) => this.deepSanitizeSignedS3Urls(products))
    )
  }

  getProductById(id: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/products/${id}`).pipe(
      map((product) => this.deepSanitizeSignedS3Urls(product))
    )
  }

  createProduct(data: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/products`, data)
  }

  getNextProductCode(storeId: string): Observable<NextProductCodeResponse> {
    return this.http.get<NextProductCodeResponse>(`${this.apiUrl}/products/next-code?store=${encodeURIComponent(storeId)}`)
  }

  updateProduct(id: string, data: any): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/products/${id}`, data)
  }

  // Uploads
  getUploadPresign(payload: { fileName: string; fileType: string }): Observable<{ uploadUrl: string; publicUrl: string }> {
    return this.http.post<{ uploadUrl: string; publicUrl: string }>(`${this.apiUrl}/uploads/presign`, payload)
  }

  uploadFileToS3(file: File): Observable<string> {
    const uploadMeta = this.normalizeUploadMeta(file)

    return this.getUploadPresign(uploadMeta).pipe(
      switchMap(({ uploadUrl, publicUrl }) => {
        const resolvedPublicUrl = this.resolvePublicUrl(uploadUrl, publicUrl)
        return this.putFileToSignedUrl(uploadUrl, file, uploadMeta.fileType).pipe(
          map(() => resolvedPublicUrl),
          catchError((err) => {
            if (!this.isExpiredSignedUrlError(err)) {
              return throwError(() => err)
            }

            return this.getUploadPresign(uploadMeta).pipe(
              switchMap(({ uploadUrl: retryUrl, publicUrl: retryPublicUrl }) => {
                const resolvedRetryPublicUrl = this.resolvePublicUrl(retryUrl, retryPublicUrl)
                return this.putFileToSignedUrl(retryUrl, file, uploadMeta.fileType).pipe(
                  map(() => resolvedRetryPublicUrl)
                )
              })
            )
          })
        )
      })
    )
  }

  // Statistics
  getStats(): Observable<Stats> {
    return this.http.get<Stats>(`${this.apiUrl}/stats`)
  }

  // Store Signup
  storeSignup(payload: StoreSignupPayload): Observable<StoreSignupResponse> {
    return this.http.post<StoreSignupResponse>(`${this.apiUrl}/store-signup`, payload)
  }

  private putFileToSignedUrl(uploadUrl: string, file: File, fileType: string): Observable<unknown> {
    return this.http.put(uploadUrl, file, {
      headers: new HttpHeaders({
        'Content-Type': fileType
      }),
      responseType: 'text'
    })
  }

  private isExpiredSignedUrlError(err: unknown): boolean {
    if (!(err instanceof HttpErrorResponse)) return false
    if (err.status !== 403) return false

    const payload = typeof err.error === 'string' ? err.error : ''
    return (
      payload.includes('Request has expired') ||
      payload.includes('<Code>AccessDenied</Code>') ||
      payload.includes('<Code>SignatureDoesNotMatch</Code>')
    )
  }

  private resolvePublicUrl(uploadUrl: string, publicUrl: string | undefined): string {
    const candidate = String(publicUrl || '').trim()
    if (candidate && !this.isSignedUrl(candidate)) return candidate
    if (!this.isSignedUrl(uploadUrl)) return uploadUrl

    try {
      const cleaned = new URL(uploadUrl)
      cleaned.search = ''
      return cleaned.toString()
    } catch {
      return uploadUrl.split('?')[0]
    }
  }

  private isSignedUrl(url: string): boolean {
    return /[?&]X-Amz-Algorithm=|[?&]X-Amz-Signature=|[?&]X-Amz-Credential=/i.test(String(url || ''))
  }

  private deepSanitizeSignedS3Urls<T>(value: T): T {
    if (Array.isArray(value)) {
      return value.map((item) => this.deepSanitizeSignedS3Urls(item)) as T
    }

    if (value && typeof value === 'object') {
      const entries = Object.entries(value as Record<string, unknown>)
      const sanitized = entries.reduce<Record<string, unknown>>((acc, [key, raw]) => {
        acc[key] = this.deepSanitizeSignedS3Urls(raw)
        return acc
      }, {})
      return sanitized as T
    }

    if (typeof value === 'string') {
      return this.sanitizeSignedPutObjectUrl(value) as T
    }

    return value
  }

  private sanitizeSignedPutObjectUrl(url: string): string {
    const input = String(url || '').trim()
    if (!input) return input
    if (!this.isSignedUrl(input)) return input
    if (!/[?&]x-id=PutObject(?:&|$)/i.test(input)) return input

    try {
      const parsed = new URL(input)
      parsed.search = ''
      return parsed.toString()
    } catch {
      return input.split('?')[0]
    }
  }

  private normalizeUploadMeta(file: File): { fileName: string; fileType: string } {
    const type = (file.type || '').trim().toLowerCase().startsWith('image/')
      ? file.type.trim().toLowerCase()
      : 'image/jpeg'

    const extensionByType: Record<string, string> = {
      'image/jpeg': '.jpg',
      'image/png': '.png',
      'image/webp': '.webp',
      'image/gif': '.gif'
    }

    const fallbackExt = extensionByType[type] || '.jpg'
    const rawName = (file.name || '').trim() || `upload-${Date.now()}${fallbackExt}`
    const safeBase = rawName.replace(/[^a-zA-Z0-9._-]/g, '_')
    const hasExt = /\.[a-zA-Z0-9]+$/.test(safeBase)
    const withExt = hasExt ? safeBase : `${safeBase}${fallbackExt}`
    const maxLen = 180
    const normalizedName = withExt.length > maxLen ? withExt.slice(withExt.length - maxLen) : withExt

    return { fileName: normalizedName, fileType: type }
  }
}
